// `useResendCooldown` — pure 60s cooldown hook backed by sessionStorage.
//
// DESIGN.md §1.3 + §4.3 (Verify-Pending): on click, persist `cooldownUntil`
// keyed by email so the cooldown survives a page refresh on the same tab.
// O2: while cooling down we flip the button label to "Resend in {n}s".

import { useCallback, useEffect, useRef, useState } from 'react';

export interface ResendCooldownState {
  /** Seconds remaining; 0 when the cooldown is inactive. */
  remaining: number;
  /** Convenience flag: `remaining > 0`. */
  active: boolean;
  /** Start a new cooldown. Persists to sessionStorage when `key` is set. */
  start: (durationMs?: number) => void;
}

const DEFAULT_DURATION_MS = 60_000;

function readStored(key: string | undefined): number {
  if (!key) return 0;
  if (typeof window === 'undefined') return 0;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeStored(key: string | undefined, until: number): void {
  if (!key) return;
  if (typeof window === 'undefined') return;
  try {
    if (until <= Date.now()) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, String(until));
    }
  } catch {
    // sessionStorage may throw in private mode — silently ignore.
  }
}

/**
 * @param key Optional sessionStorage key. When set, the cooldown is rehydrated
 *   on mount and persisted on `start`. Pass `undefined` for purely in-memory
 *   cooldowns (e.g., a test helper).
 */
export function useResendCooldown(key?: string): ResendCooldownState {
  const [until, setUntil] = useState<number>(() => readStored(key));
  // `tick` is a monotonic counter — we bump it once per second to trigger a
  // re-render so `remaining` (computed below from Date.now()) refreshes.
  const [, setTick] = useState<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Re-hydrate when the key flips (e.g., user switches email).
  useEffect(() => {
    setUntil(readStored(key));
  }, [key]);

  // Tick once per second while a cooldown is active. We deliberately use a
  // wall-clock comparison so the timer is robust against tab throttling.
  useEffect(() => {
    if (until <= Date.now()) {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTick((n) => n + 1);
      if (until <= Date.now()) {
        setUntil(0);
      }
    }, 250);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [until]);

  const start = useCallback(
    (durationMs: number = DEFAULT_DURATION_MS): void => {
      const next = Date.now() + durationMs;
      writeStored(key, next);
      setUntil(next);
    },
    [key],
  );

  const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000));

  return { remaining, active: remaining > 0, start };
}
